import { useState, useEffect, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  FileText, Upload, Trash2, Download, StickyNote, Plus, Loader2, Save, Lock, ShieldCheck,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtSize = (b) => (b > 1024 * 1024 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round(b / 1024))} KB`);
const fmtDate = (s) => new Date(s).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });

export default function Vault() {
  const [tab, setTab] = useState("documents");
  const [files, setFiles] = useState([]);
  const [notes, setNotes] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ title: "", content: "" });
  const [savingNote, setSavingNote] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [f, n] = await Promise.all([
        axios.get(`${API}/vault/files`),
        axios.get(`${API}/vault/notes`),
      ]);
      setFiles(f.data);
      setNotes(n.data);
    } catch {
      toast.error("Could not load your vault.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 15 * 1024 * 1024) { toast.error("File too large (max 15 MB)."); return; }
    const form = new FormData();
    form.append("file", file);
    setUploading(true);
    try {
      await axios.post(`${API}/vault/files`, form, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Document added to your vault");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const downloadFile = async (f) => {
    try {
      const res = await axios.get(`${API}/vault/files/${f.id}/download`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: f.content_type }));
      const a = document.createElement("a");
      a.href = url; a.download = f.original_filename;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
    } catch { toast.error("Download failed."); }
  };

  const deleteFile = async (id) => {
    try { await axios.delete(`${API}/vault/files/${id}`); setFiles((p) => p.filter((x) => x.id !== id)); toast.success("Removed"); }
    catch { toast.error("Could not remove file."); }
  };

  const addNote = async () => {
    if (!draft.title.trim() && !draft.content.trim()) return;
    setSavingNote(true);
    try {
      const { data } = await axios.post(`${API}/vault/notes`, draft);
      setNotes((p) => [data, ...p]);
      setDraft({ title: "", content: "" });
      toast.success("Secure note saved");
    } catch { toast.error("Could not save note."); }
    finally { setSavingNote(false); }
  };

  const saveNote = async (note) => {
    try { await axios.put(`${API}/vault/notes/${note.id}`, { title: note.title, content: note.content }); toast.success("Note updated"); }
    catch { toast.error("Could not update note."); }
  };

  const deleteNote = async (id) => {
    try { await axios.delete(`${API}/vault/notes/${id}`); setNotes((p) => p.filter((x) => x.id !== id)); toast.success("Note deleted"); }
    catch { toast.error("Could not delete note."); }
  };

  return (
    <div data-testid="vault-section">
      <div className="mb-10 text-center border-b border-gold/25 pb-10">
        <span className="inline-flex w-14 h-14 rounded-full bg-sage/15 items-center justify-center mb-4">
          <ShieldCheck className="w-6 h-6 text-navy" strokeWidth={1.5} />
        </span>
        <p className="uppercase tracking-[0.3em] text-xs text-sage mb-2">Private &amp; encrypted at rest</p>
        <h1 className="font-serif text-4xl md:text-5xl text-navy mb-3">Digital Vault</h1>
        <p className="text-charcoal/70 max-w-lg mx-auto">
          Safely store important documents and secure notes — wills, policies, deeds, passwords and instructions — all in one trusted place for your family.
        </p>
      </div>

      <div className="flex gap-2 mb-8 justify-center">
        <button onClick={() => setTab("documents")} data-testid="vault-tab-documents"
          className={`px-5 py-2 rounded-full text-sm transition-colors ${tab === "documents" ? "bg-navy text-ivory" : "bg-white border border-gold/25 text-navy hover:bg-navy/5"}`}>
          <FileText className="w-4 h-4 inline mr-2" />Documents
        </button>
        <button onClick={() => setTab("notes")} data-testid="vault-tab-notes"
          className={`px-5 py-2 rounded-full text-sm transition-colors ${tab === "notes" ? "bg-navy text-ivory" : "bg-white border border-gold/25 text-navy hover:bg-navy/5"}`}>
          <StickyNote className="w-4 h-4 inline mr-2" />Secure Notes
        </button>
      </div>

      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-sage" /></div>
      ) : tab === "documents" ? (
        <div className="space-y-4">
          <input ref={fileRef} type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" hidden onChange={onUpload} data-testid="vault-file-input" />
          <button onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="vault-upload-btn"
            className="w-full border-2 border-dashed border-gold/40 rounded-md py-10 flex flex-col items-center gap-2 text-navy hover:border-gold/70 hover:bg-gold/5 transition-colors">
            {uploading ? <Loader2 className="w-6 h-6 animate-spin text-sage" /> : <Upload className="w-6 h-6 text-sage" />}
            <span className="font-serif text-lg">{uploading ? "Uploading…" : "Upload a document"}</span>
            <span className="text-xs text-charcoal/60">PDF, JPG, PNG, DOC, DOCX · up to 15 MB</span>
          </button>

          {files.length === 0 ? (
            <p className="text-center text-charcoal/50 py-8" data-testid="vault-empty-files">No documents yet. Add your first one above.</p>
          ) : (
            <div className="space-y-3">
              {files.map((f) => (
                <div key={f.id} data-testid={`vault-file-${f.id}`}
                  className="flex items-center gap-4 bg-white border border-gold/20 rounded-md p-4">
                  <span className="w-10 h-10 rounded-full bg-sage/15 flex items-center justify-center shrink-0">
                    <FileText className="w-5 h-5 text-navy" strokeWidth={1.5} />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-navy font-semibold truncate">{f.original_filename}</p>
                    <p className="text-xs text-charcoal/60">{fmtSize(f.size)} · {fmtDate(f.created_at)}</p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => downloadFile(f)} data-testid={`vault-download-${f.id}`}
                    className="text-navy hover:bg-navy/5"><Download className="w-4 h-4" /></Button>
                  <Button variant="ghost" size="icon" onClick={() => deleteFile(f.id)} data-testid={`vault-delete-file-${f.id}`}
                    className="text-destructive hover:bg-destructive/5"><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-6">
          <div className="bg-white border border-gold/20 rounded-md p-5">
            <div className="flex items-center gap-2 mb-3 text-navy">
              <Lock className="w-4 h-4 text-sage" /><span className="font-serif text-lg">New secure note</span>
            </div>
            <Input value={draft.title} onChange={(e) => setDraft({ ...draft, title: e.target.value })}
              placeholder="Title (e.g., Bank login, Safe combination)" data-testid="vault-note-title"
              className="mb-3 bg-ivory border-gold/25 focus-visible:ring-sage" />
            <Textarea value={draft.content} onChange={(e) => setDraft({ ...draft, content: e.target.value })}
              placeholder="Sensitive details or instructions…" rows={3} data-testid="vault-note-content"
              className="bg-ivory border-gold/25 focus-visible:ring-sage resize-none" />
            <Button onClick={addNote} disabled={savingNote} data-testid="vault-add-note-btn"
              className="mt-3 bg-gold hover:bg-gold/90 text-navy rounded-full">
              {savingNote ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}Save note
            </Button>
          </div>

          {notes.length === 0 ? (
            <p className="text-center text-charcoal/50 py-4" data-testid="vault-empty-notes">No secure notes yet.</p>
          ) : notes.map((note, idx) => (
            <div key={note.id} data-testid={`vault-note-${note.id}`} className="bg-white border border-gold/20 rounded-md p-5">
              <Input value={note.title}
                onChange={(e) => setNotes((p) => p.map((x, i) => i === idx ? { ...x, title: e.target.value } : x))}
                className="mb-3 font-serif text-lg text-navy bg-ivory border-gold/25 focus-visible:ring-sage" />
              <Textarea value={note.content} rows={3}
                onChange={(e) => setNotes((p) => p.map((x, i) => i === idx ? { ...x, content: e.target.value } : x))}
                className="bg-ivory border-gold/25 focus-visible:ring-sage resize-none" />
              <div className="flex gap-2 mt-3">
                <Button onClick={() => saveNote(note)} data-testid={`vault-save-note-${note.id}`}
                  variant="outline" className="rounded-full border-sage text-navy hover:bg-sage/10">
                  <Save className="w-4 h-4 mr-2" />Save
                </Button>
                <Button onClick={() => deleteNote(note.id)} data-testid={`vault-delete-note-${note.id}`}
                  variant="ghost" className="rounded-full text-destructive hover:bg-destructive/5">
                  <Trash2 className="w-4 h-4 mr-2" />Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
